<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="800">
        <v-card>
            <v-card-title id="heading" class="d-flex justify-space-between">
                <div>
                    <h4>{{ productSku?.product_name }}'s OTA Components</h4>
                    <v-btn class="me-3" color="black" size="small" @click="componentDialogSwitch = true">
                        Add Component
                    </v-btn>
                </div>
                <v-btn density="compact" icon="mdi-close" variant="flat"
                       @click="$emit('update:modelValue', false)"/>
            </v-card-title>

            <v-container>
                <v-row>
                    <v-col cols="12">
                        <v-table>
                            <thead>
                            <tr>
                                <th-btn :sortedColumn="sortedColumn" column="module_index" sortable title="Module Index"
                                        @sort="sort"/>
                                <th-btn :sortedColumn="sortedColumn" column="name" sortable title="Name"
                                        @sort="sort"/>
                                <th-btn :sortedColumn="sortedColumn" column="type" sortable title="Type"
                                        @sort="sort"/>
                                <th-btn :sortedColumn="sortedColumn" column="created_at" sortable title="Created At"
                                        @sort="sort"/>
                                <th class="text-end">Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr
                                v-for="(component, index) in components"
                                v-if="!loading && components.length"
                                :key="index"
                                class="text-left"
                            >
                                <td>{{ component.module_index }}</td>
                                <td class="text-capitalize">{{ component.name }}</td>
                                <td>{{ component.type ? 'Resource File' : 'Firmware File' }}</td>
                                <td>{{ (new Date(component.created_at)).toLocaleDateString('en',) }}</td>
                                <td class="text-end">
                                    <v-btn-group density="compact" divided variant="outlined">
                                        <v-btn
                                            class="text-warning"
                                            prepend-icon="mdi-pencil"
                                            size="x-small"
                                            @click="editComponent(component)"
                                        >Edit
                                        </v-btn>
                                        <v-btn
                                            class="text-error"
                                            prepend-icon="mdi-delete"
                                            size="x-small"
                                            @click="deleteComponent(component)"
                                        >Delete
                                        </v-btn>
                                    </v-btn-group>
                                </td>
                            </tr>
                            <tr v-else>
                                <td v-if="loading" class="text-center" colspan="6">
                                    <v-progress-circular color="primary" indeterminate/>
                                </td>
                                <td v-else class="text-center" colspan="6">No record found</td>
                            </tr>
                            </tbody>
                        </v-table>
                    </v-col>
                </v-row>

                <v-row v-if="totalPages > 1" justify="center">
                    <v-col cols="8">
                        <v-pagination
                            v-model="currentPage"
                            :length="totalPages"
                            class="my-4"
                            density="compact"
                            @update:model-value="fetchDevices"
                        ></v-pagination>
                    </v-col>
                </v-row>
            </v-container>

            <v-card-actions class="px-4 d-flex justify-end">
                <v-btn
                    size="small"
                    type="button"
                    @click="$emit('update:modelValue', false);"
                >
                    Close
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <Component v-if="componentDialogSwitch" v-model="componentDialogSwitch" :component="selectedComponent"
               :productSku="productSku.id" @addComponent="(component)=> components.unshift(component)"
               @updateComponent="(component)=> {
                   selectedComponent = null;
                   components.map((item,index)=>{
                       if(item.id === component.id){
                           components[index] = component;
                       }
                   })
               }"/>
</template>

<script setup>
import {ref, onMounted} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import Component from "@/components/dialogs/ota/components/Create_Edit.vue";
import ThBtn from "@/components/globals/th-btn.vue";

const {showSnackbar} = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
    productSku: Object,
});
const sortedType = ref(null);
const sortedColumn = ref(null);
const dialogSwitch = ref(props.modelValue);
const loading = ref(true);
const totalPages = ref(1);
const currentPage = ref(1);
const components = ref([]);
const selectedComponent = ref(null);
const componentDialogSwitch = ref(false);

const emits = defineEmits(["update:modelValue"]);
onMounted(() => {
    fetchComponents();
});

const sort = (sorting) => {
    sortedColumn.value = sorting.column;
    sortedType.value = sorting.order;
    fetchComponents();
}

const fetchComponents = () => {
    loading.value = true;
    const data = {
        page: currentPage.value,
        order: sortedType.value,
        sort: sortedColumn.value,
        product_sku: props.productSku.id
    };
    send("get", `admin/ota/components`, data)
        .then((response) => {
            components.value = response.data.data.data;
            totalPages.value = response.data.data.last_page;
            currentPage.value = response.data.data.current_page;
        })
        .catch((error) => {
            showSnackbar(error.data.message ?? "Components could not be loaded", "error");
        }).finally(() => loading.value = false);
}
const editComponent = (component) => {
    selectedComponent.value = component;
    componentDialogSwitch.value = true;
}
const deleteComponent = (component) => {
    if (confirm(`Are you sure to remove Component: "${component.name}" along with all attached files?`)) {
        send("delete", `admin/ota/components/${component.id}`, null)
            .then((response) => {
                if (response.data.success) {
                    components.value.map((comp, index) => {
                        if (comp.id === component.id) {
                            components.value.splice(index, 1);
                        }
                    });
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                console.log(error);
                showSnackbar(`"${component.name}" component could not be deleted`, "error");
            });
    }
}
</script>
